// ═══════════════════════════════════════════════════════════════════════════
// generateShippingPDF — extraído VERBATIM de index.html v0.9.16.
//
// Único cambio respecto del original: la instanciación del documento pasa a
// inyectarse (crearDoc), para poder grabar los comandos de dibujo en pruebas.
// Ni una coordenada, ni una fuente, ni un color fueron tocados.
// ═══════════════════════════════════════════════════════════════════════════

import { LOGO_BASE64 } from "./logo.js";
import { etiquetaFolio } from "../datos/remisiones-pg.js";
import { guardarPDF } from "./guardar.js";
import { REMITENTE, fmt } from "./comunes.js";

function crearDocPredeterminado() {
  const { jsPDF } = window.jspdf;
  return new jsPDF({ unit: "mm", format: "letter" });
}

export function generateShippingPDF(invoiceData, products, crearDoc = crearDocPredeterminado) {
  const doc = crearDoc();
  const pw = doc.internal.pageSize.getWidth();
  const ml = 18;
  const mr = pw - 18;
  let y = 18;

  const gold = [180, 150, 80];
  const dark = [40, 40, 40];
  const gray = [120, 120, 120];
  const lightGray = [200, 200, 200];
  const teal = [100, 180, 180];

  const hline = (yy, color = lightGray) => { doc.setDrawColor(...color); doc.setLineWidth(0.3); doc.line(ml, yy, mr, yy); };
  const fullW = mr - ml;
  const colW = (pw / 2) - ml - 3;

  // ── HEADER ──
  const logoW = 22; const logoH = 22.5;
  try { doc.addImage(LOGO_BASE64, "JPEG", ml, y - 4, logoW, logoH); } catch(e) {}
  const txX = ml + logoW + 6;
  doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(...teal);
  doc.text("GUÍA DE ENVÍO", txX, y);
  doc.setFontSize(10); doc.setTextColor(...gray);
  const dateStr = invoiceData.date
    ? new Date(invoiceData.date).toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" })
    : new Date().toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" });
  doc.text(dateStr, txX, y + 6);
  doc.setFontSize(9); doc.setTextColor(...gray);
  doc.text("FOLIO:", mr, y - 4, { align: "right" });
  doc.setFontSize(22); doc.setTextColor(...gold); doc.setFont("helvetica", "bold");
  doc.text(etiquetaFolio(invoiceData), mr, y + 4, { align: "right" });
  y += Math.max(16, logoH - 2);
  hline(y); y += 8;

  // ── ADDRESSES ──
  const midX = pw / 2;
  doc.setFontSize(7); doc.setTextColor(...teal); doc.setFont("helvetica", "bold");
  doc.text("REMITENTE", ml, y);
  doc.text("DESTINATARIO", midX + 4, y);
  y += 5;

  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...dark);
  doc.text(REMITENTE.name, ml, y);
  const clientNameLines = doc.splitTextToSize(invoiceData.client.name, colW);
  doc.text(clientNameLines, midX + 4, y);
  y += Math.max(1, clientNameLines.length) * 4;

  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...gray);
  const remLines = [REMITENTE.street, REMITENTE.colonia + ", CP " + REMITENTE.cp, REMITENTE.city + ", " + REMITENTE.state, "Tel: " + REMITENTE.whatsapp];
  const cl = invoiceData.client;
  const destLines = [];
  if (cl.street) destLines.push(cl.street);
  if (cl.colonia || cl.cp) destLines.push((cl.colonia ? cl.colonia + ", " : "") + (cl.cp ? "CP " + cl.cp : ""));
  destLines.push((cl.city || "") + (cl.state ? ", " + cl.state : ""));
  if (cl.att) destLines.push("Att: " + cl.att);
  if (cl.phone) destLines.push("Tel: " + cl.phone);

  const wrapL = (lines) => { const w = []; lines.forEach(l => { if (l) doc.splitTextToSize(l, colW).forEach(wl => w.push(wl)); }); return w; };
  const rW = wrapL(remLines); const dW = wrapL(destLines);
  const mx = Math.max(rW.length, dW.length);
  for (let i = 0; i < mx; i++) {
    if (rW[i]) doc.text(rW[i], ml, y);
    if (dW[i]) doc.text(dW[i], midX + 4, y);
    y += 4;
  }
  y += 4; hline(y); y += 8;

  // ── BOX CALCULATION ──
  const totalBottles = invoiceData.totalBottles || invoiceData.lines.reduce((s, l) => s + l.qty, 0);
  const fullBoxes = Math.floor(totalBottles / 12);
  const remaining = totalBottles % 12;
  const halfBoxes = Math.floor(remaining / 6);
  const looseBottles = remaining % 6;

  // Base value: cheapest tinto grossPrice
  const tintos = products.filter(p => p.category === "tinto" && p.grossPrice > 0);
  const basePrice = tintos.length > 0 ? Math.min(...tintos.map(p => p.grossPrice)) : products.filter(p => p.grossPrice > 0).reduce((m, p) => Math.min(m, p.grossPrice), Infinity);
  const fullBoxValue = Math.round(basePrice * 12 * 100) / 100;
  const halfBoxValue = Math.round(basePrice * 6 * 100) / 100;
  const looseValue = Math.round(basePrice * looseBottles * 100) / 100;

  // ── TABLE ──
  doc.setFillColor(245, 243, 238); doc.rect(ml, y - 3, mr - ml, 8, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...dark);
  const tblCols = { desc: ml + 2, qty: ml + 90, unit: ml + 110, value: mr - 2 };
  doc.text("DESCRIPCIÓN", tblCols.desc, y);
  doc.text("CANT.", tblCols.qty, y);
  doc.text("BOTELLAS", tblCols.unit, y);
  doc.text("VALOR DECLARADO", tblCols.value, y, { align: "right" });
  y += 8;
  hline(y, lightGray); y += 5;

  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...dark);
  const fmt = (n) => "$" + n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  let totalDeclared = 0;
  const addRow = (desc, qty, bottles, value) => {
    if (qty <= 0) return;
    doc.text(desc, tblCols.desc, y);
    doc.text(String(qty), tblCols.qty, y);
    doc.text(String(bottles), tblCols.unit, y);
    doc.text(fmt(value), tblCols.value, y, { align: "right" });
    totalDeclared += value;
    y += 6;
  };

  addRow("Caja completa (12 botellas)", fullBoxes, fullBoxes * 12, fullBoxes * fullBoxValue);
  addRow("Media caja (6 botellas)", halfBoxes, halfBoxes * 6, halfBoxes * halfBoxValue);
  addRow("Botellas individuales", looseBottles, looseBottles, looseValue);

  y += 2; hline(y, dark); y += 6;

  // ── TOTALS ──
  doc.setFont("helvetica", "bold"); doc.setFontSize(10);
  doc.text("TOTAL BULTOS: " + (fullBoxes + halfBoxes + (looseBottles > 0 ? 1 : 0)), ml + 2, y);
  doc.text("TOTAL BOTELLAS: " + totalBottles, ml + 80, y);
  doc.setTextColor(...teal);
  doc.text("VALOR DECLARADO: " + fmt(totalDeclared), tblCols.value, y, { align: "right" });
  y += 12;

  // ── CONTENT DETAIL ──
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...gray);
  doc.text("Contenido: Vino de mesa / Bebidas alcohólicas embotelladas", ml, y);
  y += 4;
  doc.text("Manejo: FRÁGIL — No exponer al sol — Mantener en posición vertical", ml, y);
  y += 8;

  // ── PRODUCT SUMMARY ──
  doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...dark);
  doc.text("DETALLE DE PRODUCTOS", ml, y); y += 5;
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...gray);
  (invoiceData.lines || []).forEach(li => {
    if (li.qty > 0) {
      doc.text(li.qty + "x " + (li.productName || ""), ml + 2, y);
      y += 4;
      if (y > doc.internal.pageSize.getHeight() - 30) { doc.addPage(); y = 20; }
    }
  });

  y += 6;
  // ── NOTES ──
  if (invoiceData.notes) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...dark);
    doc.text("Notas:", ml, y);
    doc.setFont("helvetica", "normal");
    const noteLines = doc.splitTextToSize(invoiceData.notes, fullW - 14);
    doc.text(noteLines.slice(0, 3), ml + 12, y);
    y += noteLines.slice(0, 3).length * 4 + 4;
  }

  // ── FOOTER ──
  doc.setFontSize(7); doc.setTextColor(180, 180, 180);
  doc.text("Vinícola Total · Guía de Envío · Folio #" + etiquetaFolio(invoiceData), pw / 2, doc.internal.pageSize.getHeight() - 8, { align: "center" });

  const fileName = "GuiaEnvio_" + etiquetaFolio(invoiceData) + "_" + (cl.name || "").replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ ]/g, "").replace(/ +/g, "_").substring(0, 30) + ".pdf";
  guardarPDF(doc, fileName);
}
